import { motion } from 'motion/react';
import styled from 'styled-components';

import breakpoints, { under } from '@polyai/ui/styles/config/breakpoints';
import themeColors from '@polyai/ui/styles/config/colors';
import sizes from '@polyai/ui/styles/config/sizes';
import themeSpacing from '@polyai/ui/styles/config/spacing';
import text from '@polyai/ui/styles/config/text';

import PolyaiLogoSlate from 'assets/svg/companyLogos/polyai-logo-slate-rgb.svg';
import PolyaiLogoWhite from 'assets/svg/companyLogos/polyai-logo-white-rgb.svg';

export const RightPanel = styled.div`
  position: relative;

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;

  padding: 1.5rem;

  background: ${themeColors.background};
`;

export const Content = styled.div`
  overflow: hidden;
  display: flex;
  flex: 1;
  flex-direction: column;
  justify-content: center;

  width: 100%;
  max-width: ${sizes.wizardContentWidth}px;
  height: 100%;

`;

export const ScrollableSection = styled.div`
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: ${themeSpacing.sm2};

  max-height: 60vh;
  margin-bottom: ${themeSpacing.sm2};
`;

export const FixedBottomSection = styled.div`
  display: flex;
  flex: 0 0 auto;
  flex-direction: column;
  gap: ${themeSpacing.sm2};

  background: ${themeColors.background};
`;

export const Title = styled.h2`
  margin: 0 0 ${themeSpacing.sm2};
  font-size: ${text.size.header.large};
  font-weight: ${text.weight.semibold};
  color: ${themeColors.textPrimary};
`;

export const List = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${themeSpacing.sm2};

  @media ${under(breakpoints.sm)} {
    grid-template-columns: 1fr;
  }
`;

export const OrbRow = styled.div`
  display: grid;
  place-items: center;

  width: 100%;
  height: clamp(80px, 20vw, 120px);
  margin: ${themeSpacing.md2} 0;
`;

export const Card = styled.div<{ $dimmed?: boolean; $selected?: boolean }>`
  cursor: pointer;

  display: grid;
  gap: ${themeSpacing.xs3};

  padding: ${themeSpacing.sm1};

  opacity: ${p => (p.$dimmed ? 0.9 : 1)};
  border: 1px solid
    ${p => (p.$selected ? themeColors.borderStrong : themeColors.borderSubtle)};
  border-radius: 12px;

  &:hover {
    background-color: ${themeColors.backgroundHover};
    border-color: ${themeColors.borderStrong};
  }
`;

export const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

export const Meta = styled.div`
  display: grid;
  gap: ${themeSpacing.xs1};
`;

export const Subtitle = styled.div`
  font-size: ${text.size.body.small};
  color: ${themeColors.textHelper};
`;

export const VoiceActions = styled.div`
  display: flex;
  gap: ${themeSpacing.xs3};
  align-items: center;
`;

export const CustomizeSection = styled.div`
  display: grid;
  gap: ${themeSpacing.xs2};
  padding-top: ${themeSpacing.xs2};
`;

export const TextAreaContainer = styled.div`
  position: relative;

  textarea {
    resize: none;

    overflow-y: auto;

    width: 100%;
    min-height: 10vh;
    max-height: 15vh;

    border-radius: ${themeSpacing.xs3};
  }
`;

export const ShuffleIconButton = styled.button`
  cursor: pointer;

  position: absolute;
  z-index: 0;
  top: ${themeSpacing.xs2};
  right: ${themeSpacing.xs2};

  display: flex;
  align-items: center;
  justify-content: center;

  width: 32px;
  height: 32px;

  color: ${themeColors.textSecondary};

  background: ${themeColors.background};
  border: 1px solid ${themeColors.borderSubtle};
  border-radius: ${themeSpacing.xs3};

  transition: background-color 0.2s ease, border-color 0.2s ease,
    color 0.2s ease, transform 0.2s ease;

  &:hover {
    color: ${themeColors.textPrimary};
    background: ${themeColors.backgroundHover};
    border-color: ${themeColors.borderStrong};
  }

  &:active {
    background: ${themeColors.backgroundPrimary};
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

export const CustomizeLabel = styled.div`
  display: inline-flex;
  gap: ${themeSpacing.xs2};
  align-items: center;

  font-size: ${text.size.body.regular};
  color: ${themeColors.textPrimary};
`;

export const FooterCenter = styled.div`
  display: grid;
  place-items: center;
  padding-top: ${themeSpacing.sm1};
`;

export const FooterSticky = styled(FooterCenter)`
  display: flex;
  flex-direction: row;
  gap: ${themeSpacing.sm2};
  justify-content: center;

  padding-bottom: ${themeSpacing.sm1};
`;

export const Collapse = styled.div<{ $open: boolean }>`
  overflow: hidden;
  max-height: ${p => (p.$open ? '600px' : '0')};
  opacity: ${p => (p.$open ? 1 : 0)};
  transition: max-height 220ms ease, opacity 180ms ease;

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

export const Logo = styled(PolyaiLogoWhite)`
  position: absolute;
  z-index: 1;
  top: ${themeSpacing.sm3};
  left: ${themeSpacing.sm3};
  transform: translateZ(0);

  display: block;

  width: auto;
  height: ${themeSpacing.sm4};

  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-rendering: geometricprecision;

  shape-rendering: geometricprecision;
`;

export const MobileLogo = styled(PolyaiLogoSlate)`
  position: absolute;
  top: ${themeSpacing.sm3};
  left: ${themeSpacing.sm3};
  transform: translateZ(0);

  display: none;

  width: auto;
  height: ${themeSpacing.sm4};

  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-rendering: geometricprecision;

  shape-rendering: geometricprecision;

  @media ${under(breakpoints.sm)} {
    display: none; /* Hide on small screens to avoid overlap with progress slider */
  }
`;

export const LoadingContainer = styled.div`
  display: grid;
  gap: ${themeSpacing.xs2};
  place-items: center;
  min-height: 60vh;
`;

export const LoadingText = styled.div`
  font-size: ${text.size.header.large};
  font-weight: ${text.weight.semibold};
  line-height: 1.2;
`;

export const ShimmerText = styled(motion.div)`
  font-size: inherit;
  font-weight: inherit;
  line-height: inherit;
  color: transparent;

  background: linear-gradient(
    90deg,
    ${themeColors.textShimmerDark},
    ${themeColors.textShimmerMid},
    ${themeColors.textShimmerLight}
  );
  background-clip: text;
  background-size: 200% 100%;
`;

export const ResetFooter = styled.div`
  position: absolute;
  z-index: 1200; /* Match layering used in AgentStudioSection */
  right: 0;
  bottom: 0;
  left: 0;

  display: flex;
  gap: ${themeSpacing.sm1};
  align-items: center;
  justify-content: flex-start;

  padding: ${themeSpacing.sm1} ${themeSpacing.sm3};

  background: ${themeColors.background};
  border-top: 1px solid ${themeColors.borderSubtle};

  transition: background-color 0.3s ease, border-color 0.3s ease;

  @media ${under(breakpoints.sm)} {
    position: fixed;
    right: 0;
    bottom: 0;
    left: 0;

    width: 100%;
  }
`;
